import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { FileValidationService, FileValidationConfig } from './file-validation.service';
import { UploadLoggingService } from './upload-logging.service';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  fileUploadingSubject = new BehaviorSubject({});
  
  constructor(
    private http: HttpClient,
    private fileValidationService: FileValidationService,
    private uploadLoggingService: UploadLoggingService
  ) {}

  /**
   * Validate file before upload
   */
  validateFile(file: File, type: string): { isValid: boolean; errors: string[] } {
    const config = this.fileValidationService.getConfigForType(type);
    return this.fileValidationService.validateFile(file, config);
  }

  /**
   * Validate multiple files before upload
   */
  validateFiles(files: File[], type: string): { isValid: boolean; errors: string[] } {
    const config = this.fileValidationService.getConfigForType(type);
    return this.fileValidationService.validateFiles(files, config);
  }

  postFile(file: any, type: string): Observable<any> {
    const startTime = Date.now();
    const logId = this.uploadLoggingService.logUploadStart(file.name, file.size, file.type, type);
    
    console.log('FileService: Starting file upload', { 
      fileName: file.name, 
      fileSize: file.size, 
      fileType: file.type, 
      uploadType: type,
      logId
    });
    
    // Validate file before upload
    const validation = this.validateFile(file, type);
    if (!validation.isValid) {
      const duration = Date.now() - startTime;
      this.uploadLoggingService.logValidationFailure(file.name, file.size, file.type, type, validation.errors);
      console.error('FileService: File validation failed', { 
        fileName: file.name, 
        errors: validation.errors,
        logId
      });
      const error = new Error(`File validation failed: ${validation.errors.join(', ')}`);
      this.fileUploadingSubject.next({ 
        type, 
        loading: false, 
        error: error.message 
      });
      return throwError(() => error);
    }
    
    console.log('FileService: File validation passed, proceeding with upload', { logId });
    this.fileUploadingSubject.next({ type, loading: true });
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    // Don't set Content-Type header - let the browser set it for multipart/form-data
    const headers = new HttpHeaders({
      // Remove any content-type header to let browser set multipart/form-data
    });
    
    return this.http.post(`${environment.apiUrl}/files/upload`, formData, { 
      headers,
      // Ensure the request is sent as multipart/form-data
      reportProgress: true
    }).pipe(
      catchError((error) => {
        const duration = Date.now() - startTime;
        this.uploadLoggingService.logUploadFailure(logId, error.error?.message || error.message || 'Upload failed', duration);
        console.error('FileService: Upload error', { 
          fileName: file.name, 
          error: error.error || error.message, 
          status: error.status,
          statusText: error.statusText,
          logId,
          duration
        });
        this.fileUploadingSubject.next({ 
          type, 
          loading: false, 
          error: error.error?.message || error.message || 'Upload failed' 
        });
        return throwError(() => error);
      })
    );
  }
}
